import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { useSite } from "@/lib/site";

const LINKS = [["Services", "/services"], ["Pricing", "/pricing"], ["Work", "/work"], ["About", "/about"], ["Contact", "/contact"]];

export const Logo = ({ light }) => (
  <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
    <span className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-blue-600 text-white font-display font-extrabold text-sm leading-none whitespace-nowrap transition-transform duration-300 group-hover:rotate-6">
      C<span className="text-sky-300">G</span>
    </span>
    <span className={`font-display font-bold tracking-tight text-[17px] ${light ? "text-white" : "text-slate-900"}`}>
      Click &amp; Grow<span className="text-blue-600"> Digital</span>
    </span>
  </Link>
);

const MobileMenu = ({ tel, phone }) => (
  <div className="lg:hidden max-w-7xl mx-auto mt-2 rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200 p-3 shadow-xl" data-testid="nav-mobile-menu">
    {LINKS.map(([l, to]) => (
      <NavLink key={to} to={to} data-testid={`nav-mobile-link-${l.toLowerCase()}`} className="block px-4 py-3 rounded-xl text-lg font-display font-semibold text-slate-800 hover:bg-slate-100">{l}</NavLink>
    ))}
    <a href={tel} data-testid="nav-mobile-phone" className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-blue-600 text-white py-3.5 font-semibold">
      <Phone className="w-4 h-4" /> Call {phone}
    </a>
  </div>
);

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on(); window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  return scrolled;
};

export default function Navbar() {
  const { content } = useSite();
  const [open, setOpen] = useState(false);
  const scrolled = useScrolled();
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  const phone = content.phone || "+1 323 786 1615";
  const tel = `tel:${phone.replace(/[^+\d]/g, "")}`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-5 pt-3">
      <nav className={`max-w-7xl mx-auto flex items-center justify-between gap-4 rounded-2xl px-4 sm:px-5 h-16 border transition-[background-color,box-shadow,border-color] duration-300 ${scrolled || open ? "bg-white/80 backdrop-blur-xl border-slate-200 shadow-[0_8px_30px_rgba(15,23,42,0.06)]" : "bg-white/50 backdrop-blur-md border-transparent"}`}>
        <Logo />
        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map(([l, to]) => (
            <NavLink key={to} to={to} data-testid={`nav-link-${l.toLowerCase()}`}
              className={({ isActive }) => `px-3.5 py-2 rounded-full text-[15px] transition-colors ${isActive ? "text-blue-600 bg-blue-50" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
              {l}
            </NavLink>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-3">
          <a href={tel} data-testid="nav-phone-link" className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-600 transition-colors">
            <Phone className="w-4 h-4" /> {phone}
          </a>
          <Link to="/contact" data-testid="nav-cta-btn" className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-blue-600 transition-colors">
            Start a project <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <button className="lg:hidden p-2 rounded-xl hover:bg-slate-100" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>
      {open && <MobileMenu tel={tel} phone={phone} />}
    </header>
  );
}
